// =============================================================================
// FILE: packages/renderer/src/components/dashboard/HolographicEngineCore.tsx
// PURPOSE: IP-protection "shroud" + live child-license orbit visualizer.
//   Inner rings are purely decorative. The OUTER orbit ring is DATA-DRIVEN:
//   each child-license seat the parent has issued appears as a named node.
//   Online nodes glow teal; offline nodes are dimmed gold.
//   Up to 30 nodes (CHILD_SEAT_CAP). Real-time: refreshes every 20 s.
// =============================================================================

import { type JSX, useEffect, useState, useCallback } from 'react';
import { Lock, ShieldCheck, Users } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store.js';

export interface ChildSeat {
  child_email: string;
  child_name:  string;
  status:      string;
  online:      boolean;
  created_at:  string;
}

interface SeatData {
  used:      number;
  cap:       number;
  remaining: number;
  level:     string;
  children:  ChildSeat[];
}

const CX = 400, CY = 220;
const INNER_ORBIT_R = 118;
const OUTER_ORBIT_R = 178;   // child-license orbit

function getInitials(nameOrEmail: string): string {
  const name = nameOrEmail.includes('@') ? (nameOrEmail.split('@')[0] ?? '') : nameOrEmail;
  const parts = name.replace(/[._-]/g, ' ').split(' ').filter(Boolean);
  if (parts.length >= 2) return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function nodeColor(seat: ChildSeat): string {
  return seat.online ? '#5eead4' : 'rgba(252,211,77,0.55)';
}

function getBackendUrl(): string {
  return (import.meta.env.VITE_BACKEND_URL as string) ?? '';
}

export function HolographicEngineCore({
  isMaster,
  onReveal,
}: {
  isMaster: boolean;
  onReveal: () => void;
}): JSX.Element {
  const user   = useAuthStore((s) => s.user);
  const domain = user?.email?.split('@')[1] ?? '';

  const [seatData, setSeatData] = useState<SeatData | null>(null);
  const [hoveredSeat, setHoveredSeat] = useState<ChildSeat | null>(null);

  const fetchSeats = useCallback(async () => {
    if (!domain) return;
    try {
      const r = await fetch(`${getBackendUrl()}/api/intercom/seats-live/${encodeURIComponent(domain)}`);
      if (r.ok) setSeatData(await r.json() as SeatData);
    } catch { /* offline */ }
  }, [domain]);

  useEffect(() => {
    void fetchSeats();
    const id = setInterval(() => void fetchSeats(), 20_000);
    return () => clearInterval(id);
  }, [fetchSeats]);

  // Listen for child-license events fired by SeatsWidget so the core updates immediately
  useEffect(() => {
    const handler = () => void fetchSeats();
    window.addEventListener('r2:child-license-issued', handler);
    return () => window.removeEventListener('r2:child-license-issued', handler);
  }, [fetchSeats]);

  const seats  = seatData?.children ?? [];
  const used   = seatData?.used ?? 0;
  const cap    = seatData?.cap  ?? 30;
  const conduits = Array.from({ length: 12 }, (_, i) => (i * 360) / 12);
  const innerOrbit = Array.from({ length: 6 }, (_, i) => (i * 360) / 6);

  // Evenly distribute child seats around the outer orbit ring
  const seatNodes = seats.map((seat, i) => {
    const angle = (i * 360) / Math.max(seats.length, 1) - 90; // start at top
    const rad   = (angle * Math.PI) / 180;
    return {
      seat,
      x: CX + OUTER_ORBIT_R * Math.cos(rad),
      y: CY + OUTER_ORBIT_R * Math.sin(rad),
      angle,
    };
  });

  return (
    <div
      data-testid="engine-core-shroud"
      className="relative w-full overflow-hidden rounded-xl border border-zinc-800/80"
      style={{ background: 'radial-gradient(ellipse at center, #0a1622 0%, #05090f 70%)' }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid rgba(45,181,168,0.15)' }}>
        <ShieldCheck className="h-4 w-4 text-teal-300" />
        <span className="text-[11px] uppercase tracking-[0.22em] text-teal-300/80">RCD² Engine Core</span>
        <span className="text-[10px] text-zinc-500 ml-1">Live intelligence core · online</span>

        {/* Seat counter */}
        {used > 0 && (
          <div className="flex items-center gap-1.5 ml-2 px-2 py-0.5 rounded-full"
               style={{ background: 'rgba(45,181,168,0.08)', border: '1px solid rgba(45,181,168,0.2)' }}>
            <Users className="h-3 w-3 text-teal-300" />
            <span className="text-[10px] font-semibold text-teal-300">
              {used}/{cap} seats
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 ml-0.5"
                  style={{ boxShadow: '0 0 5px #4ade80', animation: 'pulse 2s infinite' }} />
          </div>
        )}

        {isMaster && (
          <button
            data-testid="reveal-core-btn"
            onClick={onReveal}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold uppercase tracking-[0.14em] transition"
            style={{ border: '1px solid rgba(252,211,77,0.5)', background: 'rgba(252,211,77,0.1)', color: '#fcd34d' }}
            title="Founder only — reveal the real system architecture"
          >
            <Lock className="h-3 w-3" /> Reveal Core
          </button>
        )}
      </div>

      {/* ── Tooltip for hovered seat ─────────────────────────────────────── */}
      {hoveredSeat && (
        <div className="absolute z-10 px-3 py-2 rounded-lg text-[11px] pointer-events-none"
             style={{
               top: 52, left: 16,
               background: 'rgba(8,16,28,0.95)',
               border: '1px solid rgba(45,181,168,0.35)',
               color: '#e2e8f0', minWidth: 180,
             }}>
          <div className="font-bold" style={{ color: '#5eead4' }}>{hoveredSeat.child_name}</div>
          <div className="text-zinc-400 mt-0.5">{hoveredSeat.child_email}</div>
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: hoveredSeat.online ? '#4ade80' : '#71717a' }} />
            <span style={{ color: hoveredSeat.online ? '#4ade80' : '#71717a' }}>
              {hoveredSeat.online ? 'Online' : 'Offline'}
            </span>
          </div>
          <div className="text-zinc-600 mt-0.5">
            Issued {new Date(hoveredSeat.created_at).toLocaleDateString()}
          </div>
        </div>
      )}

      {/* ── SVG Core ────────────────────────────────────────────────────── */}
      <svg viewBox="0 0 800 440" width="100%" style={{ display: 'block' }}>
        <defs>
          <radialGradient id="ec-core" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#fff7e0" /><stop offset="0.25" stopColor="#fcd34d" />
            <stop offset="0.6" stopColor="#2db5a8" /><stop offset="1" stopColor="rgba(45,181,168,0)" />
          </radialGradient>
          <linearGradient id="ec-ring" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#5eead4" /><stop offset="0.5" stopColor="#38bdf8" /><stop offset="1" stopColor="#fcd34d" />
          </linearGradient>
          <radialGradient id="ec-halo" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="rgba(45,181,168,0.35)" /><stop offset="1" stopColor="rgba(45,181,168,0)" />
          </radialGradient>
          <filter id="ec-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="5" />
          </filter>
          <filter id="node-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <pattern id="ec-grid" width="34" height="34" patternUnits="userSpaceOnUse">
            <path d="M34 0H0V34" fill="none" stroke="rgba(56,189,248,0.06)" strokeWidth="1" />
          </pattern>
          <linearGradient id="ec-glass" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="rgba(45,181,168,0.16)" /><stop offset="1" stopColor="rgba(8,20,28,0.55)" />
          </linearGradient>
          <radialGradient id="ec-btn" cx="0.5" cy="0.4" r="0.6">
            <stop offset="0" stopColor="#ccfbf1" /><stop offset="0.5" stopColor="#2db5a8" /><stop offset="1" stopColor="rgba(45,181,168,0)" />
          </radialGradient>
          <radialGradient id="ec-btn-gold" cx="0.5" cy="0.4" r="0.6">
            <stop offset="0" stopColor="#fff7e0" /><stop offset="0.5" stopColor="#fcd34d" /><stop offset="1" stopColor="rgba(252,211,77,0)" />
          </radialGradient>
          <filter id="ec-emblem-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="1.4" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        <rect x="0" y="0" width="800" height="440" fill="url(#ec-grid)" />
        <circle cx={CX} cy={CY} r="200" fill="url(#ec-halo)" />

        {/* ── OUTER orbit ring — child-license seats ───────────────────── */}
        <circle cx={CX} cy={CY} r={OUTER_ORBIT_R} fill="none"
          stroke="rgba(45,181,168,0.12)" strokeWidth="1" strokeDasharray="3 8" />

        {/* Travelling pulse on outer ring when seats exist */}
        {seats.length > 0 && (
          <circle cx={CX} cy={CY} r={OUTER_ORBIT_R} fill="none"
            stroke="rgba(94,234,212,0.35)" strokeWidth="1.5"
            strokeDasharray="15 200" strokeLinecap="round">
            <animateTransform attributeName="transform" type="rotate"
              from={`0 ${CX} ${CY}`} to={`360 ${CX} ${CY}`} dur="20s" repeatCount="indefinite" />
          </circle>
        )}

        {/* Empty seat slots (ghosted) */}
        {seats.length === 0 && Array.from({ length: 6 }, (_, i) => {
          const angle = (i * 360) / 6 - 90;
          const rad   = (angle * Math.PI) / 180;
          const nx    = CX + OUTER_ORBIT_R * Math.cos(rad);
          const ny    = CY + OUTER_ORBIT_R * Math.sin(rad);
          return (
            <circle key={i} cx={nx} cy={ny} r="8" fill="none"
              stroke="rgba(94,234,212,0.12)" strokeWidth="1" strokeDasharray="2 4" />
          );
        })}

        {/* Live child-license nodes */}
        {seatNodes.map(({ seat, x, y }, i) => {
          const color = nodeColor(seat);
          const init  = getInitials(seat.child_name || seat.child_email);
          return (
            <g key={seat.child_email}
               style={{ cursor: 'pointer' }}
               onMouseEnter={() => setHoveredSeat(seat)}
               onMouseLeave={() => setHoveredSeat(null)}>
              {/* Glow aura for online seats */}
              {seat.online && (
                <circle cx={x} cy={y} r="14" fill={`${color}18`} filter="url(#node-glow)" />
              )}
              {/* Node circle */}
              <circle cx={x} cy={y} r="11"
                fill="rgba(6,14,20,0.9)"
                stroke={color} strokeWidth={seat.online ? 1.8 : 0.8} />
              {/* Pulse for online */}
              {seat.online && (
                <circle cx={x} cy={y} r="11"
                  fill="none" stroke={color} strokeWidth="1.5" opacity="0.6">
                  <animate attributeName="r" values="11;16;11" dur="2.8s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.6;0;0.6" dur="2.8s" repeatCount="indefinite" />
                </circle>
              )}
              {/* Initials */}
              <text x={x} y={y + 4} textAnchor="middle"
                fill={color} fontSize="7.5" fontWeight="700"
                style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif', letterSpacing: '0.02em' }}>
                {init}
              </text>
            </g>
          );
        })}

        {/* ── Radiating energy conduits ───────────────────────────────── */}
        <g>
          {conduits.map((deg, i) => (
            <g key={i} transform={`rotate(${deg} ${CX} ${CY})`}>
              <line x1={CX} y1={CY} x2={CX} y2={CY - 165} stroke="rgba(45,181,168,0.12)" strokeWidth="2" />
              <line x1={CX} y1={CY} x2={CX} y2={CY - 165} stroke="url(#ec-ring)" strokeWidth="2"
                strokeDasharray="10 165" strokeLinecap="round" opacity="0.7">
                <animate attributeName="stroke-dashoffset" values="0;-175"
                  dur={`${2.4 + (i % 4) * 0.5}s`} repeatCount="indefinite" />
              </line>
            </g>
          ))}
        </g>

        {/* Outer dashed ring (slow) */}
        <g>
          <circle cx={CX} cy={CY} r="155" fill="none" stroke="url(#ec-ring)"
            strokeWidth="1.4" strokeDasharray="3 12" opacity="0.55" />
          <animateTransform attributeName="transform" type="rotate"
            from={`0 ${CX} ${CY}`} to={`360 ${CX} ${CY}`} dur="46s" repeatCount="indefinite" />
        </g>

        {/* Segmented turbine ring (reverse) */}
        <g>
          {Array.from({ length: 16 }).map((_, i) => {
            const a = (i * 360) / 16;
            return (
              <line key={i} x1={CX} y1={CY - 132} x2={CX} y2={CY - 150}
                stroke="#5eead4" strokeWidth="4" strokeLinecap="round" opacity="0.8"
                transform={`rotate(${a} ${CX} ${CY})`} />
            );
          })}
          <circle cx={CX} cy={CY} r="141" fill="none" stroke="rgba(94,234,212,0.3)" strokeWidth="1" />
          <animateTransform attributeName="transform" type="rotate"
            from={`360 ${CX} ${CY}`} to={`0 ${CX} ${CY}`} dur="30s" repeatCount="indefinite" />
        </g>

        {/* Inner gyro ring (fast) */}
        <g>
          <circle cx={CX} cy={CY} r="104" fill="none" stroke="url(#ec-ring)"
            strokeWidth="2" strokeDasharray="40 18" opacity="0.85" />
          <circle cx={CX} cy={CY} r="88" fill="none"
            stroke="rgba(252,211,77,0.5)" strokeWidth="1.4" strokeDasharray="6 10" />
          <animateTransform attributeName="transform" type="rotate"
            from={`0 ${CX} ${CY}`} to={`360 ${CX} ${CY}`} dur="18s" repeatCount="indefinite" />
        </g>

        {/* Static inner orbit nodes (decorative) */}
        <g>
          {innerOrbit.map((deg, i) => (
            <circle key={i} cx={CX} cy={CY - INNER_ORBIT_R} r={i % 2 ? 4 : 6}
              fill={i % 2 ? '#fcd34d' : '#5eead4'}
              style={{ filter: 'drop-shadow(0 0 6px currentColor)' }}
              transform={`rotate(${deg} ${CX} ${CY})`} />
          ))}
          <animateTransform attributeName="transform" type="rotate"
            from={`0 ${CX} ${CY}`} to={`-360 ${CX} ${CY}`} dur="22s" repeatCount="indefinite" />
        </g>

        {/* Pulsing core */}
        <circle cx={CX} cy={CY} r="70" fill="url(#ec-core)" filter="url(#ec-glow)">
          <animate attributeName="r" values="66;78;66" dur="3.4s" repeatCount="indefinite" />
        </circle>
        <circle cx={CX} cy={CY} r="30" fill="#fff7e0">
          <animate attributeName="opacity" values="0.9;1;0.9" dur="1.8s" repeatCount="indefinite" />
        </circle>

        {/* Scan sweep */}
        <rect x="-160" y="0" width="160" height="440" fill="rgba(94,234,212,0.05)">
          <animate attributeName="x" from="-160" to="800" dur="5s" repeatCount="indefinite" />
        </rect>

        {/* Cockpit consoles */}
        {[{ x: 14, flip: 1 }, { x: 690, flip: -1 }].map((panel, pi) => (
          <g key={pi} transform={`translate(${panel.x} 96)`}>
            <rect x="0" y="0" width="96" height="250" rx="12"
              fill="rgba(6,14,20,0.6)" stroke="rgba(45,181,168,0.28)" strokeWidth="1.2" />
            <rect x="10" y="12" width="76" height="58" rx="8"
              fill="url(#ec-glass)" stroke="rgba(94,234,212,0.35)" strokeWidth="1" />
            {[0, 1, 2, 3].map((b) => (
              <g key={b}>
                <rect x="18" y={22 + b * 11} width="60" height="4" rx="2" fill="rgba(94,234,212,0.12)" />
                <rect x="18" y={22 + b * 11} width="60" height="4" rx="2"
                  fill={b % 2 ? '#fcd34d' : '#5eead4'} opacity="0.9">
                  <animate attributeName="width" values={`${14 + b * 8};${52 - b * 6};${14 + b * 8}`}
                    dur={`${2.6 + b * 0.6 + pi * 0.4}s`} repeatCount="indefinite" />
                </rect>
              </g>
            ))}
            <circle cx="48" cy="104" r="22" fill="rgba(6,14,20,0.7)" stroke="rgba(94,234,212,0.3)" strokeWidth="1" />
            <circle cx="48" cy="104" r="22" fill="none" stroke="url(#ec-ring)"
              strokeWidth="2.4" strokeDasharray="90 140" strokeLinecap="round">
              <animateTransform attributeName="transform" type="rotate"
                from="0 48 104" to="360 48 104" dur={`${6 + pi * 2}s`} repeatCount="indefinite" />
            </circle>
            <circle cx="48" cy="104" r="3" fill="#fcd34d" />
            {[0, 1, 2, 3, 4, 5].map((k) => {
              const bx = 24 + (k % 3) * 24, by = 150 + Math.floor(k / 3) * 26;
              const gold = k % 3 === 1;
              return (
                <g key={k}>
                  <circle cx={bx} cy={by} r="9" fill="rgba(6,14,20,0.8)" stroke="rgba(148,163,184,0.25)" strokeWidth="0.8" />
                  <circle cx={bx} cy={by} r="5.5" fill={gold ? 'url(#ec-btn-gold)' : 'url(#ec-btn)'}>
                    <animate attributeName="opacity" values="0.45;1;0.45" dur={`${1.8 + k * 0.35}s`} repeatCount="indefinite" />
                  </circle>
                </g>
              );
            })}
            {[0, 1, 2].map((t) => (
              <g key={t} transform={`translate(20 ${210 + t * 12})`}>
                <rect x="0" y="0" width="30" height="8" rx="4" fill="rgba(6,14,20,0.85)" stroke="rgba(94,234,212,0.3)" strokeWidth="0.7" />
                <circle cx={t % 2 ? 24 : 6} cy="4" r="4" fill={t % 2 ? '#5eead4' : '#fcd34d'}
                  style={{ filter: 'drop-shadow(0 0 3px currentColor)' }} />
                <rect x="46" y="1" width="30" height="6" rx="3" fill="rgba(6,14,20,0.85)" stroke="rgba(94,234,212,0.3)" strokeWidth="0.7" />
                <rect x="48" y="2.5" width={12 + t * 6} height="3" rx="1.5" fill="#5eead4" opacity="0.8" />
              </g>
            ))}
          </g>
        ))}

        {/* Bottom throttle */}
        <g transform="translate(300 398)">
          <rect x="0" y="0" width="200" height="18" rx="9" fill="rgba(6,14,20,0.65)" stroke="rgba(45,181,168,0.3)" strokeWidth="1" />
          {Array.from({ length: 18 }).map((_, i) => (
            <rect key={i} x={8 + i * 10.4} y="5" width="6" height="8" rx="1.5"
              fill={i < 13 ? (i > 10 ? '#fcd34d' : '#5eead4') : 'rgba(94,234,212,0.15)'}>
              <animate attributeName="opacity" values="0.5;1;0.5"
                dur={`${1.4 + (i % 5) * 0.3}s`} repeatCount="indefinite" />
            </rect>
          ))}
        </g>

        {/* HUD brackets */}
        {[[20, 20, 1, 1], [780, 20, -1, 1], [20, 420, 1, -1], [780, 420, -1, -1]].map(([x, y, dx, dy], i) => (
          <path key={i}
            d={`M ${x} ${(y as number) + 16 * (dy as number)} L ${x} ${y} L ${(x as number) + 20 * (dx as number)} ${y}`}
            fill="none" stroke="rgba(94,234,212,0.4)" strokeWidth="1.5" />
        ))}

        {/* Seat count label above outer orbit */}
        {used > 0 && (
          <>
            <text x={CX} y={CY - OUTER_ORBIT_R - 12} textAnchor="middle"
              fill="rgba(94,234,212,0.55)" fontSize="8" fontWeight="700" letterSpacing="3">
              CHILD LICENSES · {used}/{cap}
            </text>
          </>
        )}

        <text x={CX} y="30" textAnchor="middle" fill="rgba(94,234,212,0.6)"
          fontSize="10" fontWeight="700" letterSpacing="4">
          RCD² INTELLIGENCE CORE · ONLINE
        </text>

        {/* Central emblem */}
        <g filter="url(#ec-emblem-glow)">
          <text x={CX} y={CY + 2} textAnchor="middle" fill="#04120f"
            fontSize="30" fontWeight="900" letterSpacing="1"
            style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}>
            RCD<tspan fontSize="17" dy="-11" fontWeight="800">2</tspan>
          </text>
          <text x={CX} y={CY + 20} textAnchor="middle" fill="rgba(4,18,15,0.75)"
            fontSize="7.5" fontWeight="700" letterSpacing="3.5">ENGINE CORE</text>
        </g>

        <text x={CX} y="424" textAnchor="middle" fill="rgba(148,163,184,0.5)"
          fontSize="9" letterSpacing="2">
          RCD² ENGINE · POWERED BY R2 INTELLIGENCE
        </text>
      </svg>
    </div>
  );
}
